/**
 * Shared URL helper utilities for deep link navigation.
 * Used by both task and notification navigation to translate
 * absolute external URLs into PSP-internal routes.
 */

import type { NavigateFunction } from 'react-router-dom';
import type { AppConfig } from '../types';
import { resolveResourceToApp } from '../services/resourceToAppResolver';
import { isIFrameApp } from '../services/utils';

/**
 * Check if a URL is absolute (has protocol/host).
 *
 * @example
 * isAbsoluteUrl('https://example.com/path') // true
 * isAbsoluteUrl('/path') // false
 */
export function isAbsoluteUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Extract pathname (including query parameters and hash) from an absolute URL.
 *
 * @example
 * extractPathFromUrl('https://procurement.ogintegration.us/gov/1/x/y') // '/gov/1/x/y'
 * extractPathFromUrl('https://procurement.ogintegration.us/contracts/123?tab=details') // '/contracts/123?tab=details'
 */
export function extractPathFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
  } catch (error) {
    console.warn('[URL Helpers] Failed to parse URL:', url, error);
    return url;
  }
}

/**
 * Check if a URL is already a PSP URL (points to PSP shell).
 * PSP hostnames: psp.ogov.me, psp.ogintegration.us, psp.ogstaging.us, app.opengov.com
 *
 * @example
 * isPspUrl('https://psp.ogov.me/procurement/contracts/123') // true
 * isPspUrl('https://procurement.ogintegration.us/contracts/123') // false
 */
export function isPspUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    const pspHostnames = [
      'psp.ogov.me', // Development
      'psp.ogintegration.us', // Integration
      'psp.ogstaging.us', // Staging
      'app.opengov.com', // Production
    ];

    return pspHostnames.includes(hostname);
  } catch {
    return false;
  }
}

/**
 * Rewrite an absolute deep link URL to a PSP-internal route using the app's basePath.
 *
 * @param deeplinkUri - Original deep link URI (may be absolute or relative)
 * @param basePath - App's basePath from registry (e.g., '/procurement')
 * @returns Rewritten PSP-internal path
 *
 * @example
 * rewriteDeepLinkUrl('https://procurement.ogintegration.us/gov/1/x/y', '/procurement')
 * // Returns: '/procurement/gov/1/x/y'
 *
 * rewriteDeepLinkUrl('/procurement/gov/1/x/y', '/procurement')
 * // Returns: '/procurement/gov/1/x/y' (no rewriting needed)
 *
 * rewriteDeepLinkUrl('https://psp.ogov.me/procurement/contracts/123', '/procurement')
 * // Returns: '/procurement/contracts/123' (already PSP-formatted, no basePath prepending)
 */
export function rewriteDeepLinkUrl(deeplinkUri: string, basePath: string): string {
  if (!isAbsoluteUrl(deeplinkUri)) {
    return deeplinkUri;
  }

  const pathname = extractPathFromUrl(deeplinkUri);

  // If URL is already a PSP URL, return pathname as-is (don't prepend basePath)
  if (isPspUrl(deeplinkUri)) {
    return pathname;
  }

  // External app URL - prepend app's basePath for PSP-internal routing
  return `${basePath}${pathname}`;
}

/**
 * Deep link navigation result types:
 * - 'internal': Navigate using React Router with PSP-internal route (remote modules)
 * - 'iframe': Navigate to iframe app with deep link URL via location.state
 * - 'external': Open in new tab with target="_blank"
 * - 'none': No navigation available (render as non-clickable)
 */
export interface DeepLinkNavigationResult {
  type: 'internal' | 'external' | 'iframe' | 'none';
  path?: string;
  url?: string;
  state?: { iframeUrl?: string };
}

/**
 * Core deep link resolver shared by task and notification navigation.
 * Determines navigation type based on a deep link URL, an optional resource
 * subject URN, and the app registry.
 *
 * Priority logic:
 * 1. No deepLinkUrl → 'none'
 * 2. No subject → absolute URL = 'external', relative = 'internal'
 * 3. Subject exists → resolve via app registry:
 *    - iframe app → 'iframe' with deep link URL in state
 *    - new-tab app → 'external'
 *    - remote module → rewrite URL, return 'internal'
 *    - no match → 'external'
 */
export function resolveDeepLinkNavigation(
  deepLinkUrl: string | null | undefined,
  subject: string | null | undefined,
  appRegistry: readonly AppConfig[]
): DeepLinkNavigationResult {
  if (!deepLinkUrl) {
    return { type: 'none' };
  }

  if (!subject) {
    if (isAbsoluteUrl(deepLinkUrl)) {
      return { type: 'external', url: deepLinkUrl };
    }
    return { type: 'internal', path: deepLinkUrl };
  }

  const app = resolveResourceToApp(subject, appRegistry);

  if (!app) {
    return { type: 'external', url: deepLinkUrl };
  }

  if (isIFrameApp(app)) {
    if (isAbsoluteUrl(deepLinkUrl)) {
      return {
        type: 'iframe',
        path: app.basePath,
        state: { iframeUrl: deepLinkUrl },
      };
    }
    console.warn(
      '[Deep Link Navigation] Iframe deep link is relative, using default URL:',
      deepLinkUrl
    );
    return { type: 'iframe', path: app.basePath };
  }

  // Only a NON-EMPTY externalUrl means external, matching isIFrameApp/
  // isNewTabApp: a dual-topology entry (PLC, App Builder) resolves externalUrl
  // to '' when it mounts in-shell, and those deep links must be rewritten as
  // internal module routes — not forced into a new tab.
  if (app.externalUrl) {
    return { type: 'external', url: deepLinkUrl };
  }

  const rewrittenPath = rewriteDeepLinkUrl(deepLinkUrl, app.basePath);
  return { type: 'internal', path: rewrittenPath };
}

/**
 * Performs navigation for a resolved deep link result.
 * Internal and iframe results navigate via React Router; external results open in a new tab.
 */
export function performDeepLinkNavigation(
  result: DeepLinkNavigationResult,
  navigate: NavigateFunction
): void {
  if (result.type === 'internal' && result.path) {
    navigate(result.path);
  } else if (result.type === 'iframe' && result.path) {
    navigate(result.path, { state: result.state });
  } else if (result.type === 'external' && result.url) {
    window.open(result.url, '_blank', 'noopener,noreferrer');
  }
}
